import { DestroyRef, Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { interval } from 'rxjs';
import { RateLimitService, RateLimitInfo } from '../../services/rate-limit.service';

@Component({
  selector: 'app-rate-limit-alert',
  standalone: true,
  imports: [],
  template: `
    @if (rateLimitInfo) {
      <!-- KAN-595: z-[10000] clears the learner course player, whose overlay
           layers are all z-index 9999. At z-50 this alert was painted entirely
           behind an open player, same as the error toast beside it in
           app.component.html. -->
      <div class="fixed bottom-4 right-4 max-w-md z-[10000]">
        <div class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-lg">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium">Rate Limit Exceeded</h3>
              <div class="mt-2 text-sm">
                <p>Too many requests to <code class="font-mono">{{ rateLimitInfo.endpoint }}</code></p>
                <p class="mt-1">
                  Retry in: <span class="font-semibold">{{ formatTimeRemaining() }}</span>
                </p>
                <p class="mt-1 text-xs text-red-600">
                  Limit: {{ rateLimitInfo.limit }} requests per {{ formatWindow(rateLimitInfo.windowMs) }}
                </p>
              </div>
            </div>
            <div class="ml-auto">
              <button
                (click)="dismiss()"
                class="inline-flex text-red-400 hover:text-red-500 focus:outline-none focus:text-red-500 transition ease-in-out duration-150"
                >
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    }
    `,
  styles: []
})
export class RateLimitAlertComponent implements OnInit {
  rateLimitInfo: RateLimitInfo | null = null;
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private timeRemaining = 0;

  constructor(private rateLimitService: RateLimitService) {}

  ngOnInit(): void {
    // Subscribe to rate limit status
    this.rateLimitService.rateLimitStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(info => {
        this.rateLimitInfo = info;
        if (info) {
          this.updateTimeRemaining();
        }
        this.cdr.markForCheck();
      });

    // Update time remaining every second
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.rateLimitInfo) {
          this.updateTimeRemaining();
          this.cdr.markForCheck();
        }
      });
  }
  private updateTimeRemaining(): void {
    if (!this.rateLimitInfo) return;
    
    const now = new Date().getTime();
    const blockedUntil = this.rateLimitInfo.blockedUntil.getTime();
    this.timeRemaining = Math.max(0, Math.ceil((blockedUntil - now) / 1000));
    
    // Auto-hide when time expires
    if (this.timeRemaining === 0) {
      this.rateLimitInfo = null;
    }
  }

  formatTimeRemaining(): string {
    if (this.timeRemaining <= 0) return 'now';
    
    if (this.timeRemaining < 60) {
      return `${this.timeRemaining} seconds`;
    } else if (this.timeRemaining < 3600) {
      const minutes = Math.ceil(this.timeRemaining / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      const hours = Math.ceil(this.timeRemaining / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
  }

  formatWindow(windowMs: number): string {
    const seconds = windowMs / 1000;
    if (seconds < 60) {
      return `${seconds} seconds`;
    } else if (seconds < 3600) {
      const minutes = seconds / 60;
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      const hours = seconds / 3600;
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
  }

  dismiss(): void {
    this.rateLimitInfo = null;
  }
}